import { Injectable, computed, inject, signal } from '@angular/core';
import {
  ChessFinisher, CyclathonEvent, CyclathonRider, EntryUnit, GalleryImage, GallerySection, GoalEvent, Match,
  MatchDetails, MatchStatus, Participant, Round, ScoreEntryStyle, SideType, Sport, SportCategory,
  SportIconKey, SportMode, Standing, StandingPlace, Team,
} from '../models/tournament.models';
import { RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { CYCLATHON_DETAILS } from '../data/cyclathon-event';
import { eventDayOf } from '../data/tournament-clock';
import { SupabaseService } from './supabase.service';
import { VENUE_PLACEHOLDER } from '../../shared/util/match-readiness';

// ---------------------------------------------------------------------------
// Row shapes, as the database returns them (see supabase/migrations)
// ---------------------------------------------------------------------------

interface SportRow {
  id: string; name: string; slug: string; mode: SportMode; score_entry_style: ScoreEntryStyle;
  icon: SportIconKey; blurb: string;
  sport_categories: CategoryRow[];
}
interface CategoryRow {
  id: string; sport_id: string; label: string; participant_count: number; entry_unit: EntryUnit;
  format: string; external_app_url: string | null; live_stream_url: string | null;
  chess_finishers: { rank: number; name: string; reg_no: string }[];
}
interface StandingRow { category_id: string; place: StandingPlace; entry_id: string | null; name: string }
interface RoundRow { code: string; label: string; sort_order: number }
interface TeamRow { id: string; sport_id: string; category_id: string; name: string; short_name: string; reg_no: string }
interface ParticipantRow { id: string; sport_id: string; category_id: string; name: string; reg_no: string; team_id: string | null }
interface GoalRow {
  id: string; side: 'A' | 'B'; minute: number; scorer_name: string; assist_name: string | null;
  own_goal: boolean; penalty: boolean;
}
interface MatchRow {
  id: string; sport_id: string; category_id: string; round_code: string;
  round: { label: string; sort_order: number } | null;
  court_or_venue: string; scheduled_at: string; status: MatchStatus; side_type: SideType;
  side_a_id: string | null; side_a_name: string; side_a_reg_no: string | null;
  side_b_id: string | null; side_b_name: string; side_b_reg_no: string | null;
  winner_side_id: string | null; result_note: string | null;
  external_scorecard_url: string | null; live_stream_url: string | null;
  version: number;
  goal_events: GoalRow[];
}
interface RiderRow { reg_no: string; name: string; category: string | null }
interface GalleryRow { id: string; section_id: string; storage_path: string; uploaded_at: string }
interface GallerySectionRow { id: string; name: string; sort_order: number }

/** Minimal shape of a Supabase/PostgREST error. */
interface DbError { code?: string; message: string }

type Loader = 'sports' | 'rounds' | 'matches' | 'teams' | 'participants' | 'cyclathon' | 'gallery';

/**
 * Small lists that are reloaded whole when their table changes. Fixtures, goals,
 * teams, participants and photographs are not listed: with a thousand visitors
 * open at once, reloading those whole on every change would be a thousand full
 * downloads per save, so they are updated row by row instead (see listen()).
 */
const LOADER_FOR_TABLE: Record<string, Loader> = {
  sports: 'sports', sport_categories: 'sports', chess_finishers: 'sports', category_standings: 'sports',
  rounds: 'rounds',
  cyclathon_riders: 'cyclathon',
  gallery_sections: 'gallery', gallery_settings: 'gallery',
};

/** A fixture with the two things stored elsewhere: its round's name and its goals. */
const MATCH_SELECT = '*, round:rounds(label, sort_order), goal_events(*)';

/** Fixtures fetched per request when only some changed; keeps the URL short. */
const MATCH_FETCH_BATCH = 100;

/** A tab hidden at least this long refreshes everything when it is shown again. */
const STALE_AFTER_HIDDEN_MS = 60_000;

/** Refreshes of everything closer together than this are skipped. */
const MIN_FULL_REFRESH_GAP_MS = 10_000;

/**
 * When the live-update connection drops for everyone at once and comes back,
 * each browser waits a random moment up to this long before refreshing, so a
 * thousand refreshes don't reach the database in the same second.
 */
const RECONNECT_JITTER_MS = 5_000;

/** Storage bucket the gallery's photographs are uploaded to. */
const GALLERY_BUCKET = 'gallery';

/** Supabase returns at most this many rows per request. */
const PAGE_SIZE = 1000;

/** Thrown when someone else saved a match after this editor opened it. */
export class MatchConflictError extends Error {}

export type LoadState = 'loading' | 'ready' | 'error';

/**
 * TOURNAMENT DATA
 * ---------------
 * Everything the app shows comes from Supabase through this service, and every
 * staff change goes back through it. Components read the signals below and call
 * the async methods; none of them talk to the database directly.
 *
 * After each write the affected list is reloaded, and Realtime reloads it in
 * every other open browser, so the database is the only source of truth.
 */
@Injectable({ providedIn: 'root' })
export class TournamentDataService {
  private readonly db = inject(SupabaseService).db;

  private readonly _sports = signal<Sport[]>([]);
  private readonly _rounds = signal<Round[]>([]);
  private readonly _teams = signal<Team[]>([]);
  private readonly _participants = signal<Participant[]>([]);
  private readonly _matches = signal<Match[]>([]);
  private readonly _cyclathon = signal<CyclathonEvent>({ ...CYCLATHON_DETAILS, riders: [] });
  private readonly _gallery = signal<GalleryImage[]>([]);
  private readonly _gallerySections = signal<GallerySection[]>([]);
  private readonly _galleryDriveUrl = signal<string | null>(null);
  private readonly _loadState = signal<LoadState>('loading');

  readonly sports = this._sports.asReadonly();
  readonly rounds = this._rounds.asReadonly();
  readonly teams = this._teams.asReadonly();
  readonly participants = this._participants.asReadonly();
  readonly matches = this._matches.asReadonly();
  readonly cyclathon = this._cyclathon.asReadonly();
  /** Newest first. */
  readonly gallery = this._gallery.asReadonly();
  /** In the order the admin set. */
  readonly gallerySections = this._gallerySections.asReadonly();
  /** The "View all images" Google Drive link; null until the admin sets it. */
  readonly galleryDriveUrl = this._galleryDriveUrl.asReadonly();
  readonly loadState = this._loadState.asReadonly();

  /** Per-list request counter, so a slow older response never overwrites a newer one. */
  private readonly generations = new Map<Loader, number>();
  private readonly reloadTimers = new Map<Loader, ReturnType<typeof setTimeout>>();
  private hasSubscribed = false;

  /** Fixtures whose rows changed, fetched together after a short pause. */
  private readonly changedMatchIds = new Set<string>();
  private matchFetchTimer?: ReturnType<typeof setTimeout>;
  /** Full fixture loads in flight, and fixtures updated while one was — see loadMatches(). */
  private fullMatchLoads = 0;
  private readonly recheckAfterFullLoad = new Set<string>();

  private lastFullRefresh = 0;

  constructor() {
    void this.loadAll();
    this.listen();
  }

  // =========================================================================
  // Derived views
  // =========================================================================

  readonly liveMatches = computed(() =>
    this._matches()
      .filter(m => m.status === 'live')
      .sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt))
  );

  readonly recentResults = computed(() =>
    this._matches()
      .filter(m => m.status === 'completed')
      .sort((a, b) => b.scheduledAt.localeCompare(a.scheduledAt))
  );

  /** Sports that actually have an in-app draw — chess and the cyclathon don't. */
  readonly fixtureSports = computed(() => this._sports().filter(s => s.mode === 'fixtures'));

  readonly totalCategories = computed(() =>
    this._sports().reduce((sum, s) => sum + s.categories.length, 0)
  );

  sportById(sportId: string): Sport | null {
    return this._sports().find(s => s.id === sportId) ?? null;
  }

  sportBySlug(slug: string): Sport | null {
    return this._sports().find(s => s.slug === slug) ?? null;
  }

  categoriesOf(sportId: string): SportCategory[] {
    return this.sportById(sportId)?.categories ?? [];
  }

  categoryById(categoryId: string): SportCategory | null {
    for (const s of this._sports()) {
      const c = s.categories.find(x => x.id === categoryId);
      if (c) return c;
    }
    return null;
  }

  matchById(matchId: string): Match | null {
    return this._matches().find(m => m.id === matchId) ?? null;
  }

  entriesOf(categoryId: string): { id: string; name: string; regNo: string }[] {
    const category = this.categoryById(categoryId);
    if (!category) return [];
    return category.entryUnit === 'team'
      ? this._teams().filter(t => t.categoryId === categoryId)
      : this._participants().filter(p => p.categoryId === categoryId);
  }

  /** Event day (1-based) a match falls on; 0 if it sits outside the event window. */
  dayOf(match: Match): number {
    return eventDayOf(match.scheduledAt);
  }

  // =========================================================================
  // Loading
  // =========================================================================

  async loadAll(): Promise<void> {
    this.lastFullRefresh = Date.now();
    try {
      await Promise.all([
        this.loadSports(), this.loadRounds(), this.loadMatches(), this.loadTeams(),
        this.loadParticipants(), this.loadCyclathon(), this.loadGallery(),
      ]);
      this._loadState.set('ready');
    } catch (err) {
      console.error('Loading tournament data failed', err);
      this._loadState.set('error');
    }
  }

  private loadSports(): Promise<void> {
    return this.latest('sports', async () => {
      const [{ data, error }, standings] = await Promise.all([
        this.db
          .from('sports')
          .select('*, sport_categories(*, chess_finishers(rank, name, reg_no))')
          .order('display_order')
          .order('display_order', { referencedTable: 'sport_categories' }),
        this.db.from('category_standings').select('category_id, place, entry_id, name'),
      ]);
      if (error) throw error;
      // Staff's corrected standings are an extra: if they can't be read, every
      // category still shows what its fixtures say, so the sports load carries on.
      if (standings.error) console.error('Loading corrected standings failed', standings.error);
      const corrected = (standings.data ?? []) as StandingRow[];
      return (data as SportRow[]).map(row => this.toSport(row, corrected));
    }, sports => this._sports.set(sports));
  }

  private loadRounds(): Promise<void> {
    return this.latest('rounds', async () => {
      const { data, error } = await this.db.from('rounds').select('*').order('sort_order');
      if (error) throw error;
      return (data as RoundRow[]).map(r => ({ code: r.code, label: r.label, sortOrder: r.sort_order }));
    }, rounds => this._rounds.set(rounds));
  }

  private async loadMatches(): Promise<void> {
    this.fullMatchLoads++;
    try {
      await this.latest('matches', async () => {
        const rows = await this.selectAll<MatchRow>((from, to) =>
          this.db
            .from('matches')
            .select(MATCH_SELECT)
            .is('deleted_at', null)
            .order('id')
            .range(from, to));
        return rows.map(row => this.toMatch(row));
      }, matches => this._matches.set(matches));
    } finally {
      this.fullMatchLoads--;
      // A fixture updated on its own while this load was running may have been
      // overwritten by the load's older copy. Fetch those again.
      if (this.fullMatchLoads === 0 && this.recheckAfterFullLoad.size) {
        for (const id of this.recheckAfterFullLoad) this.changedMatchIds.add(id);
        this.recheckAfterFullLoad.clear();
        this.scheduleMatchFetch();
      }
    }
  }

  /**
   * Fetches just these fixtures and merges them in. A fixture that no longer
   * comes back (removed) is dropped. An older copy never replaces a newer one.
   */
  private async refreshMatches(ids: string[]): Promise<void> {
    if (!ids.length) return;
    const rows: MatchRow[] = [];
    for (let i = 0; i < ids.length; i += MATCH_FETCH_BATCH) {
      const { data, error } = await this.db
        .from('matches')
        .select(MATCH_SELECT)
        .in('id', ids.slice(i, i + MATCH_FETCH_BATCH))
        .is('deleted_at', null);
      if (error) throw error;
      rows.push(...(data as MatchRow[]));
    }

    const fresh = new Map(rows.map(row => [row.id, this.toMatch(row)]));
    const asked = new Set(ids);
    this._matches.update(list => {
      const next: Match[] = [];
      for (const m of list) {
        if (!asked.has(m.id)) { next.push(m); continue; }
        const f = fresh.get(m.id);
        if (f) next.push(f.version >= m.version ? f : m);
        fresh.delete(m.id);
      }
      next.push(...fresh.values());
      // Same order as a full load (Postgres orders uuids like their text).
      return next.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    });
    if (this.fullMatchLoads > 0) for (const id of ids) this.recheckAfterFullLoad.add(id);
  }

  private queueMatchFetch(matchId: string): void {
    this.changedMatchIds.add(matchId);
    this.scheduleMatchFetch();
  }

  /** One save touches the fixture and its goals; collapse the burst into one request. */
  private scheduleMatchFetch(): void {
    clearTimeout(this.matchFetchTimer);
    this.matchFetchTimer = setTimeout(() => {
      const ids = [...this.changedMatchIds];
      this.changedMatchIds.clear();
      this.refreshMatches(ids).catch(err => console.error('Updating fixtures failed', err));
    }, 300);
  }

  private loadTeams(): Promise<void> {
    return this.latest('teams', async () => {
      const rows = await this.selectAll<TeamRow>((from, to) =>
        this.db.from('teams').select('*').order('id').range(from, to));
      return rows.map(toTeam);
    }, teams => this._teams.set(teams));
  }

  private loadParticipants(): Promise<void> {
    return this.latest('participants', async () => {
      const rows = await this.selectAll<ParticipantRow>((from, to) =>
        this.db.from('participants').select('*').order('id').range(from, to));
      return rows.map(toParticipant);
    }, participants => this._participants.set(participants));
  }

  private loadCyclathon(): Promise<void> {
    return this.latest('cyclathon', async () => {
      const riders = await this.selectAll<RiderRow>((from, to) =>
        this.db.from('cyclathon_riders').select('*').order('reg_no').range(from, to));
      return {
        ...CYCLATHON_DETAILS,
        riders: riders.map(r => ({ regNo: r.reg_no, name: r.name, category: r.category })),
      };
    }, cyclathon => this._cyclathon.set(cyclathon));
  }

  private loadGallery(): Promise<void> {
    return this.latest('gallery', async () => {
      const [images, sections, settings] = await Promise.all([
        this.selectAll<GalleryRow>((from, to) =>
          this.db.from('gallery_images').select('*').order('uploaded_at', { ascending: false }).order('id').range(from, to)),
        this.db.from('gallery_sections').select('*').order('sort_order').order('created_at'),
        this.db.from('gallery_settings').select('drive_url').maybeSingle(),
      ]);
      if (sections.error) throw sections.error;
      if (settings.error) throw settings.error;
      return {
        images: images.map(r => this.toGalleryImage(r)),
        sections: (sections.data as GallerySectionRow[]).map(s => ({ id: s.id, name: s.name, sortOrder: s.sort_order })),
        driveUrl: (settings.data as { drive_url: string | null } | null)?.drive_url ?? null,
      };
    }, gallery => {
      this._gallery.set(gallery.images);
      this._gallerySections.set(gallery.sections);
      this._galleryDriveUrl.set(gallery.driveUrl);
    });
  }

  private async latest<T>(loader: Loader, fetch: () => Promise<T>, apply: (value: T) => void): Promise<void> {
    const run = (this.generations.get(loader) ?? 0) + 1;
    this.generations.set(loader, run);
    const value = await fetch();
    if (this.generations.get(loader) === run) apply(value);
  }

  /** Pages past the 1,000-row response limit; rows beyond it would otherwise be silently dropped. */
  private async selectAll<T>(
    page: (from: number, to: number) => PromiseLike<{ data: unknown[] | null; error: DbError | null }>,
  ): Promise<T[]> {
    const rows: T[] = [];
    for (let from = 0; ; from += PAGE_SIZE) {
      const { data, error } = await page(from, from + PAGE_SIZE - 1);
      if (error) throw error;
      rows.push(...((data ?? []) as T[]));
      if (!data || data.length < PAGE_SIZE) return rows;
    }
  }

  // =========================================================================
  // Live updates
  // =========================================================================

  private listen(): void {
    const channel = this.db.channel('tournament');
    for (const [table, loader] of Object.entries(LOADER_FOR_TABLE)) {
      channel.on('postgres_changes', { event: '*', schema: 'public', table }, () => this.reloadSoon(loader));
    }

    // Row by row: only the fixture that changed is fetched.
    channel.on('postgres_changes', { event: '*', schema: 'public', table: 'matches' }, change => {
      const id = rowOf<{ id: string }>(change)?.id;
      if (id) this.queueMatchFetch(id);
    });
    channel.on('postgres_changes', { event: '*', schema: 'public', table: 'goal_events' }, change => {
      // A deleted goal arrives as just its id; find its fixture here.
      const goal = rowOf<{ id: string; match_id?: string }>(change);
      const matchId = goal?.match_id ?? (goal ? this.matchOfGoal(goal.id) : null);
      if (matchId) this.queueMatchFetch(matchId);
    });

    // The change itself carries the whole row, so nothing is fetched at all.
    channel.on('postgres_changes', { event: '*', schema: 'public', table: 'teams' }, change =>
      this._teams.update(list => applyRow<TeamRow, Team>(list, change, toTeam)));
    channel.on('postgres_changes', { event: '*', schema: 'public', table: 'participants' }, change =>
      this._participants.update(list => applyRow<ParticipantRow, Participant>(list, change, toParticipant)));
    channel.on('postgres_changes', { event: '*', schema: 'public', table: 'gallery_images' }, change =>
      this._gallery.update(list => applyRow<GalleryRow, GalleryImage>(list, change, row => this.toGalleryImage(row))
        .sort((a, b) => b.uploadedAt.localeCompare(a.uploadedAt) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))));

    channel.subscribe(status => {
      if (status !== 'SUBSCRIBED') return;
      // A reconnect means events may have been missed while offline.
      if (this.hasSubscribed) setTimeout(() => this.refreshAll(), Math.random() * RECONNECT_JITTER_MS);
      this.hasSubscribed = true;
    });

    // A tab in the background may have missed changes; only a long absence is
    // worth a full refresh (a phone switching apps for a moment is not).
    let hiddenAt = 0;
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) { hiddenAt = Date.now(); return; }
      if (hiddenAt && Date.now() - hiddenAt >= STALE_AFTER_HIDDEN_MS) this.refreshAll();
    });
    window.addEventListener('online', () => this.refreshAll());
  }

  /** Reloads everything, at most once per MIN_FULL_REFRESH_GAP_MS. */
  private refreshAll(): void {
    const now = Date.now();
    if (now - this.lastFullRefresh < MIN_FULL_REFRESH_GAP_MS) return;
    this.lastFullRefresh = now;
    void this.loadAll();
  }

  private matchOfGoal(goalId: string): string | null {
    return this._matches().find(m => m.goalEvents?.some(g => g.id === goalId))?.id ?? null;
  }

  /** One save changes several rows; collapse the burst into a single reload. */
  private reloadSoon(loader: Loader): void {
    clearTimeout(this.reloadTimers.get(loader));
    this.reloadTimers.set(loader, setTimeout(() => {
      this.reload(loader).catch(err => console.error(`Reloading ${loader} failed`, err));
    }, 300));
  }

  private reload(loader: Loader): Promise<void> {
    switch (loader) {
      case 'sports': return this.loadSports();
      case 'rounds': return this.loadRounds();
      case 'matches': return this.loadMatches();
      case 'teams': return this.loadTeams();
      case 'participants': return this.loadParticipants();
      case 'cyclathon': return this.loadCyclathon();
      case 'gallery': return this.loadGallery();
    }
  }

  // =========================================================================
  // Writes — matches
  // =========================================================================

  /** Returns the saved match, so an open editor can take the new version. */
  async setStatus(matchId: string, status: MatchStatus): Promise<Match> {
    const { error } = await this.db.rpc('set_match_status', { p_match_id: matchId, p_status: status });
    if (error) throw this.explain(error, 'The status was not changed.');
    return this.reloadMatch(matchId);
  }

  /** Marks a match completed together with its winner and result summary — a completed match always has both. */
  async completeMatch(matchId: string, winnerSideId: string, resultNote: string): Promise<Match> {
    const { error } = await this.db.rpc('complete_match', {
      p_match_id: matchId, p_winner_side_id: winnerSideId, p_result_note: resultNote,
    });
    if (error) throw this.explain(error, 'The match was not marked completed.');
    return this.reloadMatch(matchId);
  }

  /** Admin only: sets a completed match back to live, keeping its result, goals and links. */
  async reopenMatch(matchId: string): Promise<Match> {
    const { error } = await this.db.rpc('reopen_match', { p_match_id: matchId });
    if (error) throw this.explain(error, 'The match was not reopened.');
    return this.reloadMatch(matchId);
  }

  /**
   * Saves the editor's staged details and goal changes in one transaction.
   * `opened` must be the match as it was when the editor opened — goals are
   * compared with that, never with the current list, or a goal another scorer
   * just added would look like one this editor removed.
   */
  async saveMatchDetails(opened: Match, details: MatchDetails): Promise<Match> {
    const goalKey = (g: GoalEvent) =>
      [g.side, g.minute, g.scorerName.trim(), g.assistName?.trim() ?? '', !!g.penalty, !!g.ownGoal].join('|');
    const openedKeys = new Map((opened.goalEvents ?? []).map(g => [g.id, goalKey(g)]));
    const kept = new Set(details.goalEvents.filter(g => openedKeys.get(g.id) === goalKey(g)).map(g => g.id));

    const { error } = await this.db.rpc('save_match', {
      p_match_id: opened.id,
      p_expected_version: opened.version,
      p_details: {
        scheduled_at: details.scheduledAt,
        round_code: details.roundCode,
        court_or_venue: details.courtOrVenue,
        side_a_id: details.sideAId, side_a_name: details.sideAName, side_a_reg_no: details.sideARegNo,
        side_b_id: details.sideBId, side_b_name: details.sideBName, side_b_reg_no: details.sideBRegNo,
        winner_side_id: details.winnerSideId,
        result_note: details.resultNote,
        external_scorecard_url: details.externalScorecardUrl,
        live_stream_url: details.liveStreamUrl,
      },
      // An edited goal is removed and added again.
      p_remove_goal_ids: [...openedKeys.keys()].filter(id => !kept.has(id)),
      p_add_goals: details.goalEvents.filter(g => !kept.has(g.id)).map(g => ({
        side: g.side,
        minute: g.minute,
        scorer_name: g.scorerName,
        assist_name: g.assistName ?? null,
        own_goal: !!g.ownGoal,
        penalty: !!g.penalty,
      })),
    });
    if (error) throw this.explain(error, 'The fixture was not saved.');
    return this.reloadMatch(opened.id);
  }

  async addMatch(input: {
    sportId: string; categoryId: string; roundCode: string;
    courtOrVenue: string; scheduledAt: string; sideType: SideType;
  }): Promise<Match> {
    const { data, error } = await this.db
      .from('matches')
      .insert({
        sport_id: input.sportId,
        category_id: input.categoryId,
        round_code: input.roundCode,
        court_or_venue: input.courtOrVenue.trim() || VENUE_PLACEHOLDER,
        scheduled_at: input.scheduledAt,
        side_type: input.sideType,
      })
      .select('id')
      .single();
    if (error) throw this.explain(error, 'The fixture was not added.');
    return this.reloadMatch((data as { id: string }).id);
  }

  async removeMatch(matchId: string): Promise<void> {
    const { error } = await this.db.rpc('remove_match', { p_match_id: matchId });
    if (error) throw this.explain(error, 'The fixture was not removed.');
    await this.refreshMatches([matchId]);
  }

  private async reloadMatch(matchId: string): Promise<Match> {
    await this.refreshMatches([matchId]);
    const match = this.matchById(matchId);
    if (!match) throw new Error('The change was saved, but the fixture could not be reloaded. Refresh the page.');
    return match;
  }

  // =========================================================================
  // Writes — participants & teams
  // =========================================================================

  async addTeam(t: Omit<Team, 'id'>): Promise<void> {
    const { error } = await this.db.from('teams').insert({
      sport_id: t.sportId, category_id: t.categoryId, name: t.name, short_name: t.shortName, reg_no: t.regNo,
    });
    if (error) throw this.explain(error, `${t.name} was not added.`);
    await this.loadTeams();
  }

  async removeTeam(id: string): Promise<void> {
    await this.deleteOne('teams', 'id', id, 'The team was not removed.');
    await this.loadTeams();
  }

  /**
   * Adds a whole sheet of teams at once. One insert, so the upload either
   * lands completely or changes nothing — a half-imported category would be
   * worse than none at all.
   */
  async addTeams(teams: readonly Omit<Team, 'id'>[]): Promise<void> {
    if (!teams.length) return;
    const { error } = await this.db.from('teams').insert(
      teams.map(t => ({
        sport_id: t.sportId, category_id: t.categoryId, name: t.name, short_name: t.shortName, reg_no: t.regNo,
      })),
    );
    if (error) throw this.explain(error, 'No teams were added.');
    await this.loadTeams();
  }

  async addParticipant(p: Omit<Participant, 'id'>): Promise<void> {
    const { error } = await this.db.from('participants').insert({
      sport_id: p.sportId, category_id: p.categoryId, name: p.name, reg_no: p.regNo, team_id: p.teamId ?? null,
    });
    if (error) throw this.explain(error, `${p.name} was not added.`);
    await this.loadParticipants();
  }

  async removeParticipant(id: string): Promise<void> {
    await this.deleteOne('participants', 'id', id, 'The participant was not removed.');
    await this.loadParticipants();
  }

  /** Adds a whole sheet of participants at once — see addTeams for why it is one insert. */
  async addParticipants(people: readonly Omit<Participant, 'id'>[]): Promise<void> {
    if (!people.length) return;
    const { error } = await this.db.from('participants').insert(
      people.map(p => ({
        sport_id: p.sportId, category_id: p.categoryId, name: p.name, reg_no: p.regNo, team_id: p.teamId ?? null,
      })),
    );
    if (error) throw this.explain(error, 'No participants were added.');
    await this.loadParticipants();
  }

  // =========================================================================
  // Writes — chess, cyclathon, gallery
  // =========================================================================

  /** Chess: a category's scoreboard and live stream links. */
  async setCategoryLinks(categoryId: string, links: { scoreboardUrl: string; streamUrl: string }): Promise<void> {
    const { data, error } = await this.db
      .from('sport_categories')
      .update({
        external_app_url: links.scoreboardUrl.trim() || null,
        live_stream_url: links.streamUrl.trim() || null,
      })
      .eq('id', categoryId)
      .select('id');
    if (error) throw this.explain(error, 'The links were not saved.');
    if (!data?.length) throw new Error('The links were not saved: this account cannot edit that category.');
    await this.loadSports();
  }

  /** Chess: replaces a category's published top finishers. */
  async setCategoryTopFinishers(categoryId: string, topFinishers: ChessFinisher[]): Promise<void> {
    const { error } = await this.db.rpc('replace_chess_finishers', {
      p_category_id: categoryId,
      p_rows: topFinishers.map(f => ({ rank: f.rank, name: f.name, reg_no: f.regNo })),
    });
    if (error) throw this.explain(error, 'The results were not published.');
    await this.loadSports();
  }

  /**
   * Replaces a category's corrected final standings. An empty list puts the
   * category back on what its Final and Third-place play-off say.
   */
  async setCategoryStandings(categoryId: string, standings: Standing[]): Promise<void> {
    const { error } = await this.db.rpc('replace_category_standings', {
      p_category_id: categoryId,
      p_rows: standings.map(s => ({ place: s.place, entry_id: s.entryId, name: s.name })),
    });
    if (error) throw this.explain(error, 'The standings were not saved.');
    await this.loadSports();
  }

  /** Replaces the whole rider list — what an uploaded sheet does. */
  async setCyclathonRiders(riders: CyclathonRider[]): Promise<void> {
    const { error } = await this.db.rpc('replace_cyclathon_riders', {
      p_rows: riders.map(r => ({ reg_no: r.regNo, name: r.name, category: r.category ?? null })),
    });
    if (error) throw this.explain(error, 'The rider list was not published.');
    await this.loadCyclathon();
  }

  async addCyclathonRider(rider: CyclathonRider): Promise<void> {
    const { error } = await this.db.from('cyclathon_riders').insert({
      reg_no: rider.regNo, name: rider.name, category: rider.category ?? null,
    });
    if (error) throw this.explain(error, `${rider.name} was not added.`);
    await this.loadCyclathon();
  }

  async removeCyclathonRider(regNo: string): Promise<void> {
    await this.deleteOne('cyclathon_riders', 'reg_no', regNo, 'The rider was not removed.');
    await this.loadCyclathon();
  }

  /**
   * Uploads one photograph's file and publishes it under a header. Does not
   * reload the gallery, so a batch of uploads reloads once at the end — call
   * `refreshGallery()` when the batch is done.
   */
  async uploadGalleryImage(sectionId: string, file: Blob): Promise<void> {
    const extension = file.type === 'image/png' ? 'png' : file.type === 'image/webp' ? 'webp' : 'jpg';
    // Not crypto.randomUUID(): it is missing on plain-http pages, such as the dev server opened on a phone.
    const unique = `${Date.now().toString(36)}-${Array.from(crypto.getRandomValues(new Uint8Array(12)), b => b.toString(16).padStart(2, '0')).join('')}`;
    const path = `${sectionId}/${unique}.${extension}`;
    const bucket = this.db.storage.from(GALLERY_BUCKET);

    const upload = await bucket.upload(path, file, { contentType: file.type, cacheControl: '31536000', upsert: false });
    if (upload.error) throw this.explain({ message: upload.error.message }, 'The photograph was not uploaded.');

    const { error } = await this.db.from('gallery_images').insert({ section_id: sectionId, storage_path: path });
    if (error) {
      // Don't leave a file behind that no photograph points to.
      await bucket.remove([path]);
      throw this.explain(error, 'The photograph was not published.');
    }
  }

  refreshGallery(): Promise<void> {
    return this.loadGallery();
  }

  async removeGalleryImage(image: GalleryImage): Promise<void> {
    await this.deleteOne('gallery_images', 'id', image.id, 'The photograph was not removed.');
    // The photograph is already off the public page; a file left behind here is only unused storage.
    const { error } = await this.db.storage.from(GALLERY_BUCKET).remove([image.storagePath]);
    if (error) console.error('Removing a gallery file failed', error);
    await this.loadGallery();
  }

  async addGallerySection(name: string): Promise<void> {
    const sortOrder = Math.max(0, ...this._gallerySections().map(s => s.sortOrder)) + 1;
    const { error } = await this.db.from('gallery_sections').insert({ name: name.trim(), sort_order: sortOrder });
    if (error) throw this.explainSection(error, 'The header was not added.');
    await this.loadGallery();
  }

  async renameGallerySection(id: string, name: string): Promise<void> {
    const { data, error } = await this.db.from('gallery_sections').update({ name: name.trim() }).eq('id', id).select('id');
    if (error) throw this.explainSection(error, 'The header was not renamed.');
    if (!data?.length) throw new Error('The header was not renamed: it may have been removed, or this account cannot edit it.');
    await this.loadGallery();
  }

  async removeGallerySection(id: string): Promise<void> {
    try {
      await this.deleteOne('gallery_sections', 'id', id, 'The header was not removed.');
    } finally {
      await this.loadGallery();
    }
  }

  /** Saves the headers in exactly this order. */
  async reorderGallerySections(ids: string[]): Promise<void> {
    const { error } = await this.db.rpc('reorder_gallery_sections', { p_ids: ids });
    await this.loadGallery();
    if (error) throw this.explain(error, 'The headers were not reordered.');
  }

  async setGalleryDriveUrl(url: string): Promise<void> {
    const { data, error } = await this.db
      .from('gallery_settings')
      .update({ drive_url: url.trim() || null })
      .eq('id', 1)
      .select('id');
    if (error) throw this.explain(error, 'The link was not saved.');
    if (!data?.length) throw new Error('The link was not saved: this account cannot edit it.');
    await this.loadGallery();
  }

  private explainSection(error: DbError, failure: string): Error {
    if (error.code === '23505') return new Error(`${failure} A header with that name already exists.`);
    return this.explain(error, failure);
  }

  // =========================================================================
  // Helpers
  // =========================================================================

  /** A delete that Row Level Security blocks returns no error and no rows, so check for the row. */
  private async deleteOne(table: string, column: string, value: string, failure: string): Promise<void> {
    const { data, error } = await this.db.from(table).delete().eq(column, value).select(column);
    if (error) throw this.explain(error, failure);
    if (!data?.length) throw new Error(`${failure} It may already be gone, or this account cannot remove it.`);
  }

  /** Turns a database error into a message staff can act on. */
  private explain(error: DbError, failure: string): Error {
    switch (error.code) {
      case 'PT409': return new MatchConflictError(error.message);
      case 'PT404': return new Error(error.message);
      case '23505': return new Error(`${failure} That registration number or goal is already recorded.`);
      case '23514': return new Error(`${failure} A value isn't allowed: check the sides, venue, winner, result summary, minute and required fields.`);
      case '23503': return new Error(`${failure} Something else still uses it: remove that first.`);
      case '42501': return new Error(`${failure} This account isn't allowed to make that change.`);
    }
    if (/fetch|network/i.test(error.message)) {
      return new Error(`${failure} Couldn't reach the database: check the connection and try again.`);
    }
    return new Error(`${failure} ${error.message}`);
  }

  private toSport(row: SportRow, corrected: StandingRow[]): Sport {
    return {
      id: row.id,
      name: row.name,
      slug: row.slug,
      mode: row.mode,
      scoreEntryStyle: row.score_entry_style,
      icon: row.icon,
      blurb: row.blurb,
      categories: (row.sport_categories ?? []).map(c => ({
        id: c.id,
        sportId: c.sport_id,
        label: c.label,
        participantCount: c.participant_count,
        entryUnit: c.entry_unit,
        format: c.format,
        externalAppUrl: c.external_app_url,
        liveStreamUrl: c.live_stream_url ?? null,
        topFinishers: (c.chess_finishers ?? [])
          .map(f => ({ rank: f.rank, name: f.name, regNo: f.reg_no }))
          .sort((a, b) => a.rank - b.rank),
        standingsOverride: corrected
          .filter(s => s.category_id === c.id)
          .map(s => ({ place: s.place, entryId: s.entry_id, name: s.name }))
          .sort((a, b) => a.place - b.place || a.name.localeCompare(b.name)),
      })),
    };
  }

  private toGalleryImage(r: GalleryRow): GalleryImage {
    return {
      id: r.id,
      sectionId: r.section_id,
      storagePath: r.storage_path,
      url: this.db.storage.from(GALLERY_BUCKET).getPublicUrl(r.storage_path).data.publicUrl,
      uploadedAt: new Date(r.uploaded_at).toISOString(),
    };
  }

  private toMatch(row: MatchRow): Match {
    const goalEvents: GoalEvent[] = (row.goal_events ?? [])
      .map(g => ({
        id: g.id,
        side: g.side,
        minute: g.minute,
        scorerName: g.scorer_name,
        assistName: g.assist_name,
        ownGoal: g.own_goal,
        penalty: g.penalty,
      }))
      .sort((a, b) => a.minute - b.minute || a.id.localeCompare(b.id));

    return {
      id: row.id,
      sportId: row.sport_id,
      categoryId: row.category_id,
      roundCode: row.round_code,
      round: row.round?.label ?? row.round_code,
      roundOrder: row.round?.sort_order ?? 0,
      version: row.version,
      courtOrVenue: row.court_or_venue,
      // One format everywhere: the app sorts these strings with localeCompare.
      scheduledAt: new Date(row.scheduled_at).toISOString(),
      status: row.status,
      sideType: row.side_type,
      sideAId: row.side_a_id,
      sideBId: row.side_b_id,
      sideAName: row.side_a_name,
      sideBName: row.side_b_name,
      sideARegNo: row.side_a_reg_no,
      sideBRegNo: row.side_b_reg_no,
      winnerSideId: row.winner_side_id,
      resultNote: row.result_note,
      goalsScore: {
        sideAGoals: goalEvents.filter(g => g.side === 'A').length,
        sideBGoals: goalEvents.filter(g => g.side === 'B').length,
      },
      goalEvents,
      externalScorecardUrl: row.external_scorecard_url,
      liveStreamUrl: row.live_stream_url,
    };
  }
}

// ---------------------------------------------------------------------------
// Row helpers
// ---------------------------------------------------------------------------

function toTeam(r: TeamRow): Team {
  return {
    id: r.id, sportId: r.sport_id, categoryId: r.category_id,
    name: r.name, shortName: r.short_name, regNo: r.reg_no,
  };
}

function toParticipant(r: ParticipantRow): Participant {
  return {
    id: r.id, sportId: r.sport_id, categoryId: r.category_id,
    name: r.name, regNo: r.reg_no, teamId: r.team_id,
  };
}

/** The changed row: the new one, or for a delete the old one (which may hold only its id). */
function rowOf<T>(change: RealtimePostgresChangesPayload<Record<string, unknown>>): T | null {
  const row = change.eventType === 'DELETE' ? change.old : change.new;
  return row && 'id' in row ? (row as T) : null;
}

/** Applies one realtime change to a list keyed by id, keeping its order. */
function applyRow<R, T extends { id: string }>(
  list: T[],
  change: RealtimePostgresChangesPayload<Record<string, unknown>>,
  map: (row: R) => T,
): T[] {
  const id = rowOf<{ id: string }>(change)?.id;
  if (!id) return list;
  if (change.eventType === 'DELETE') return list.filter(item => item.id !== id);
  const item = map(change.new as unknown as R);
  const at = list.findIndex(existing => existing.id === id);
  if (at === -1) return [...list, item].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  const next = [...list];
  next[at] = item;
  return next;
}
